import { jsPDF } from "jspdf";
import "jspdf-autotable";
import JsBarcode from "jsbarcode";
import FESCOWaterMark from "../../../images/DiscosWater/fesco-watermark.png";
import GEPCOWaterMark from "../../../images/DiscosWater/gepco-watermark.png";
import HAZECOWaterMark from "../../../images/DiscosWater/hazeco-watermark.png";
import HESCOWaterMark from "../../../images/DiscosWater/hesco-watermark.png";
import IESCOWaterMark from "../../../images/DiscosWater/iescowatermark.png";
import LESCOWaterMark from "../../../images/DiscosWater/lescowatermark.png";
import MEPCOWaterMark from "../../../images/DiscosWater/mepcco-watermark.png";
import PESCOWaterMark from "../../../images/DiscosWater/pesco-watermark.png";
import QESCOWaterMark from "../../../images/DiscosWater/qesco-watermark.png";
import SEPCOWaterMark from "../../../images/DiscosWater/sepco-watermark.png";
import TESCOWaterMark from "../../../images/DiscosWater/tesco-watermark.png";
import {
  companyShortName,
  getLogo,
  isPdf,
  isPdfBase64Payload,
  normalizeDocumentDataUrl,
  subdivisionName,
} from "../../../services/Functions";
import { pdfToImages } from "../../../utils/pdfToImages";
import { mapDocumentsToReadingRows } from "./mrcReadingUtils";

const getWaterMarkLogo = (code) => {
  switch (String(code ?? "").slice(0, 2)) {
    case "11":
      return LESCOWaterMark;
    case "12":
      return GEPCOWaterMark;
    case "13":
      return FESCOWaterMark;
    case "14":
      return IESCOWaterMark;
    case "15":
      return MEPCOWaterMark;
    case "26":
      return PESCOWaterMark;
    case "27":
      return HAZECOWaterMark;
    case "37":
      return HESCOWaterMark;
    case "38":
      return SEPCOWaterMark;
    case "48":
      return QESCOWaterMark;
    case "59":
      return TESCOWaterMark;
    default:
      return MEPCOWaterMark;
  }
};

const formatReadingValue = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? String(n) : String(value).trim();
};

const formatMeterTypeLabel = (value) =>
  String(value || "-")
    .replace(/[\r\n]+/g, " ")
    .replace(/\s+/g, " ")
    .trim() || "-";

const formatDisplayValue = (value) =>
  value === null || value === undefined || value === "" ? "" : String(value);

const formatApplicationDate = (applicationDate) => {
  const date =
    applicationDate instanceof Date
      ? applicationDate
      : applicationDate
        ? new Date(applicationDate)
        : new Date();
  if (Number.isNaN(date.getTime())) {
    const fallback = new Date();
    return `${fallback.getDate()} ${
      [
        "Jan",
        "Feb",
        "Mar",
        "Apr",
        "May",
        "Jun",
        "Jul",
        "Aug",
        "Sep",
        "Oct",
        "Nov",
        "Dec",
      ][fallback.getMonth()]
    } ${fallback.getFullYear()}`;
  }
  return `${date.getDate()} ${
    [
      "Jan",
      "Feb",
      "Mar",
      "Apr",
      "May",
      "Jun",
      "Jul",
      "Aug",
      "Sep",
      "Oct",
      "Nov",
      "Dec",
    ][date.getMonth()]
  } ${date.getFullYear()}`;
};

/** Map getMRCDocuments rows → receipt meter rows (form + authority + site survey). */
export const mapMrcDocumentsToReceiptReadingRows = (
  documents = [],
  { isNetMeter, meterPhaseType = "" } = {}
) =>
  mapDocumentsToReadingRows(documents, {
    meterPhaseType,
    ...(isNetMeter ? { isNetMeter: true } : {}),
  }).map((row) => ({
      meterType: formatMeterTypeLabel(row.meterLabel),
      serialNo: row.meterSerialNo || "",
      previousReading: row.previous ?? "",
      asChargedReading: row.asCharged ?? "",
      claimedReading: row.claimed ?? "",
      authorityReading: row.authorityClaimed ?? "",
      siteSurveyReading: row.siteSurveyReading ?? "",
      setNo: row.setNo,
      claimedImage: row.imageSrc || "",
      claimedImageThumbnail: row.imageSrc || "",
      siteSurveyImage: row.siteSurveyImageSrc || "",
      siteSurveyImageThumbnail: row.siteSurveyImageSrc || "",
    })
  );

const hasReadableValue = (value) =>
  value !== null && value !== undefined && String(value).trim() !== "";

const drawEvidencePageHeader = ({
  doc,
  title,
  caption,
  pageWidth,
  pageHeight,
  footerReserve,
}) => {
  doc.addPage();
  let y = 16;

  if (title) {
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(title, pageWidth / 2, y, { align: "center" });
    y += 8;
  }

  if (caption) {
    doc.setFontSize(9);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(40, 40, 40);
    doc.text(caption, pageWidth / 2, y, { align: "center" });
    y += 7;
  }

  const marginX = 10;
  const boxX = marginX;
  const boxY = y;
  const boxW = pageWidth - marginX * 2;
  const boxH = pageHeight - boxY - footerReserve - 4;

  return { boxX, boxY, boxW, boxH };
};

/** Fit image inside the box without stretching / compressing aspect ratio. */
const addContainedImage = (doc, src, format, boxX, boxY, boxW, boxH) => {
  let drawW = boxW;
  let drawH = boxH;
  try {
    const props = doc.getImageProperties(src);
    const iw = Number(props?.width) || 0;
    const ih = Number(props?.height) || 0;
    if (iw > 0 && ih > 0) {
      const scale = Math.min(boxW / iw, boxH / ih);
      drawW = iw * scale;
      drawH = ih * scale;
    }
  } catch (e) {
    // keep box size if properties are unavailable
  }

  const x = boxX + (boxW - drawW) / 2;
  const y = boxY + (boxH - drawH) / 2;
  doc.addImage(src, format, x, y, drawW, drawH, undefined, "NONE");
  return { x, y, w: drawW, h: drawH };
};

const drawFittedEvidence = (doc, src, format, box) => {
  const { x, y, w, h } = addContainedImage(
    doc,
    src,
    format,
    box.boxX,
    box.boxY,
    box.boxW,
    box.boxH
  );
  doc.setDrawColor(200, 205, 212);
  doc.setLineWidth(0.3);
  doc.rect(x - 0.5, y - 0.5, w + 1, h + 1);
};

const drawEvidencePlaceholder = (doc, box, message, pageWidth) => {
  doc.setFont("helvetica", "normal");
  doc.setFontSize(9);
  doc.setTextColor(100, 100, 100);
  doc.text(message, pageWidth / 2, box.boxY + 16, { align: "center" });
  doc.setTextColor(0, 0, 0);
};

const renderEvidenceSection = async ({
  doc,
  rows,
  title,
  getImageSrc,
  pageWidth,
  pageHeight,
  footerReserve = 34,
}) => {
  const evidenceRows = (rows || []).filter((row) => getImageSrc(row));
  if (!evidenceRows.length) return;

  for (let index = 0; index < evidenceRows.length; index += 1) {
    const row = evidenceRows[index];
    const meterLabel = String(row.meterType || "")
      .replace(/\n/g, " ")
      .trim();
    const setLabel = formatDisplayValue(row.setNo);
    const caption = [meterLabel, setLabel ? `Set ${setLabel}` : ""]
      .filter(Boolean)
      .join(" — ");

    const rawSrc = getImageSrc(row);
    const src = normalizeDocumentDataUrl(rawSrc) || String(rawSrc || "");
    const base64Body = src.includes(",")
      ? src.slice(src.indexOf(",") + 1)
      : src.replace(/^data:[^;]+;base64,/i, "");
    const isPdfEvidence = isPdf(src) || isPdfBase64Payload(base64Body);

    if (isPdfEvidence) {
      const pdfPages = await pdfToImages(src);
      const pagesToDraw = pdfPages.length ? pdfPages : [null];

      for (let pageIndex = 0; pageIndex < pagesToDraw.length; pageIndex += 1) {
        const pageCaption =
          pageIndex === 0
            ? caption || title
            : `${caption || title} (Continued)`;
        const box = drawEvidencePageHeader({
          doc,
          title,
          caption: pageCaption,
          pageWidth,
          pageHeight,
          footerReserve,
        });

        if (!pagesToDraw[pageIndex]) {
          drawEvidencePlaceholder(doc, box, "(PDF attached)", pageWidth);
          continue;
        }

        try {
          drawFittedEvidence(doc, pagesToDraw[pageIndex], "PNG", box);
        } catch (e) {
          drawEvidencePlaceholder(doc, box, "(Unable to render PDF)", pageWidth);
        }
      }
      continue;
    }

    const box = drawEvidencePageHeader({
      doc,
      title,
      caption: caption || title,
      pageWidth,
      pageHeight,
      footerReserve,
    });

    try {
      const body = String(base64Body || "").replace(/\s/g, "");
      const format = /^iVBORw0KGgo/i.test(body) ? "PNG" : "JPEG";
      drawFittedEvidence(doc, src, format, box);
    } catch (e) {
      drawEvidencePlaceholder(doc, box, "(Unable to render image)", pageWidth);
    }
  }
};

/**
 * Same PDF receipt generated on MRC form submission.
 */
export const printMrcApplicationReceipt = async ({
  values = {},
  applicationID,
  issuedBy,
  referenceNo,
  readingRows = [],
  companyCode = "",
  userCompany = "",
  subDivisions = [],
  applicationDate = null,
} = {}) => {
  const discoCode = String(values.iv_sub_divison || companyCode || "").slice(
    0,
    2
  );
  const doc = new jsPDF();
  const barcodeCanvas = document.createElement("canvas");
  JsBarcode(barcodeCanvas, String(applicationID));
  const barcodeDataURL = barcodeCanvas.toDataURL("image/png");

  let height = 10;
  const padding = 10;
  const smallLogoCodes = ["12", "13", "11", "16", "26", "27", "48", "59"];
  const pageWidth = doc.internal.pageSize.getWidth();
  const logoWidth = smallLogoCodes.includes(discoCode) ? 25 : 30;
  const logoHeight = smallLogoCodes.includes(discoCode) ? 15 : 8;
  const logoX = (pageWidth - logoWidth) / 2;

  doc.addImage(getLogo(discoCode), "PNG", logoX, height, logoWidth, logoHeight);

  height = discoCode === "91" ? height + 30 : height + 20;

  doc.setFontSize(15);
  doc.setFont("helvetica", "bold");
  doc.text(
    "Receipt of Meter Reading Correction Request",
    pageWidth / 2,
    height,
    { align: "center" }
  );

  const subDivCode = String(values.iv_sub_divison || "");
  const subDivDisplay = subDivCode
    ? `${subDivCode} / ${subdivisionName(subDivCode, subDivisions)}`
    : "-";

  const summaryCell = (text, bold = false) => ({
    content: String(text ?? "-"),
    styles: {
      fontStyle: bold ? "bold" : "normal",
      textColor: [0, 0, 0],
    },
  });

  doc.autoTable({
    startY: height + 12,
    theme: "plain",
    styles: {
      font: "helvetica",
      fontSize: 10,
      cellPadding: { top: 3.8, bottom: 3.8, left: 0, right: 5 },
      overflow: "linebreak",
      valign: "middle",
      textColor: [0, 0, 0],
    },
    columnStyles: {
      0: { cellWidth: 40, fontStyle: "bold" },
      1: { cellWidth: 52 },
      2: { cellWidth: 58, fontStyle: "bold" },
      3: { cellWidth: 40 },
    },
    body: [
      [
        summaryCell("DISCO / Company:", true),
        summaryCell(
          companyShortName(
            String(values.iv_circle || discoCode || "").slice(0, 2)
          )
        ),
        summaryCell("Sub Division Code & Name:", true),
        summaryCell(subDivDisplay),
      ],
      [
        summaryCell("Tracking ID:", true),
        summaryCell(applicationID),
        summaryCell("Application Date:", true),
        summaryCell(formatApplicationDate(applicationDate)),
      ],
      [
        summaryCell("Reference No:", true),
        summaryCell(referenceNo || "-"),
        summaryCell("Mobile No.:", true),
        summaryCell(values.iv_applicant_phone_no || "-"),
      ],
      [
        summaryCell("Applicant Name:", true),
        summaryCell(values.iv_applicant_name || "-"),
        summaryCell("Issued By:", true),
        summaryCell(issuedBy || "-"),
      ],
    ],
    margin: { left: padding, right: padding },
  });

  height = doc.lastAutoTable?.finalY || height + 40;

  const rowsForReceipt = (Array.isArray(readingRows) ? readingRows : []).filter(
    (row) =>
      hasReadableValue(row?.previousReading) ||
      hasReadableValue(row?.asChargedReading) ||
      hasReadableValue(row?.claimedReading) ||
      hasReadableValue(row?.authorityReading) ||
      hasReadableValue(row?.siteSurveyReading) ||
      row?.serialNo
  );

  const showAuthorityCol = rowsForReceipt.some((row) =>
    hasReadableValue(row?.authorityReading)
  );
  const showSiteSurveyCol = rowsForReceipt.some((row) =>
    hasReadableValue(row?.siteSurveyReading)
  );

  const readingHead = [
    "Meter Type",
    "Serial No.",
    "Previous Reading",
    "As Charged Reading",
    "User Claimed Reading",
  ];
  if (showSiteSurveyCol) readingHead.push("Site Survey Reading");
  if (showAuthorityCol) readingHead.push("Authority Reading");

  const readingTableRows = rowsForReceipt.map((row) => {
    const cells = [
      formatMeterTypeLabel(row.meterType),
      String(row.serialNo || "").trim() || "-",
      formatReadingValue(row.previousReading) || "-",
      formatReadingValue(row.asChargedReading) || "-",
      formatReadingValue(row.claimedReading) || "-",
    ];
    if (showSiteSurveyCol) {
      cells.push(formatReadingValue(row.siteSurveyReading) || "-");
    }
    if (showAuthorityCol) {
      cells.push(formatReadingValue(row.authorityReading) || "-");
    }
    return cells;
  });

  let contentBottomY = height;
  if (readingTableRows.length > 0) {
    height += 16;
    doc.setFont("helvetica", "bold");
    doc.text("Meter Reading Details:", padding, height);

    doc.autoTable({
      startY: height + 6,
      head: [readingHead],
      body: readingTableRows,
      theme: "grid",
      styles: {
        fontSize: readingHead.length > 5 ? 7 : 8,
        halign: "center",
        valign: "middle",
        overflow: "linebreak",
        cellPadding: 1.2,
      },
      headStyles: { fillColor: [66, 66, 66], textColor: 255 },
      columnStyles: {
        0: { halign: "left", cellWidth: 42, fontStyle: "bold" },
      },
      margin: { left: padding, right: padding, bottom: 34 },
    });
    contentBottomY = doc.lastAutoTable?.finalY || height + 30;
  }

  const pageHeight = doc.internal.pageSize.getHeight();
  const FOOTER_RESERVE = 34;
  // Watermark must match the application's DISCO (subdivision / ref),
  // not the logged-in user's office (esp. admin company code 10).
  const watermarkCompany = String(
    values.iv_sub_divison ||
      values.iv_circle ||
      discoCode ||
      companyCode ||
      referenceNo ||
      userCompany ||
      ""
  ).slice(0, 2);

  await renderEvidenceSection({
    doc,
    rows: rowsForReceipt,
    title: "Consumer Evidence",
    getImageSrc: (row) => row?.claimedImageThumbnail || row?.claimedImage || "",
    pageWidth,
    pageHeight,
    footerReserve: FOOTER_RESERVE,
  });

  await renderEvidenceSection({
    doc,
    rows: rowsForReceipt,
    title: "Site Survey Evidence",
    getImageSrc: (row) =>
      row?.siteSurveyImageThumbnail || row?.siteSurveyImage || "",
    pageWidth,
    pageHeight,
    footerReserve: FOOTER_RESERVE,
  });

  if (watermarkCompany !== "91") {
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.addImage(
        getWaterMarkLogo(watermarkCompany),
        "PNG",
        0,
        0,
        doc.internal.pageSize.getWidth(),
        doc.internal.pageSize.getHeight()
      );
    }
  }

  {
    const totalPages = doc.internal.getNumberOfPages();
    const barcodeW = 40;
    const barcodeH = 16;
    const barcodeX = (pageWidth - barcodeW) / 2;
    const barcodeY = pageHeight - 28;

    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      doc.addImage(barcodeDataURL, "PNG", barcodeX, barcodeY, barcodeW, barcodeH);
    }
  }

  const pdfBlob = doc.output("blob");
  const pdfDataUrl = URL.createObjectURL(pdfBlob);
  window.open(pdfDataUrl, "_blank");
};

export default printMrcApplicationReceipt;
